/**
 * Prioris — Spec Catalog page.
 *
 * Top-level Spec Library landing page. Drills down:
 *   Jurisdictions → Documents → Tables → Rubrica picker + Properties grid
 *
 * On "Apply to project" the selected rubricas for the chosen table are
 * persisted so other modules (Materials Approval evaluation) can consume them.
 */
import { useEffect, useState } from 'react';
import { fetchSln } from '../nexus/nexusHelpers';
import { useAuth } from '../../contexts/AuthContext';
import {
  Loader2, ChevronRight, ArrowLeft, ShieldCheck, BookOpenCheck, CheckSquare, Square,
  Save, Info, Languages, Layers, Sparkles, Trash2,
} from 'lucide-react';

// Simple PT ⇄ EN toggle state persisted in localStorage
function useLang() {
  const [lang, setLang] = useState(() => localStorage.getItem('prioris-spec-lang') || 'pt');
  useEffect(() => { localStorage.setItem('prioris-spec-lang', lang); }, [lang]);
  return [lang, setLang];
}
const L = (obj, key, lang) => (lang === 'en' ? (obj[`${key}_en`] || obj[key]) : obj[key]);  // eslint-disable-line no-unused-vars

export default function SpecCatalogPage({ t, project }) {
  const [view, setView] = useState('jurisdictions');  // jurisdictions | documents | tables | table
  const [jkey, setJkey] = useState(null);
  const [dkey, setDkey] = useState(null);
  const [tkey, setTkey] = useState(null);
  const [lang, setLang] = useLang();
  const [refreshKey, setRefreshKey] = useState(0);

  const go = (v, extra = {}) => {
    if (v === 'jurisdictions') { setJkey(null); setDkey(null); setTkey(null); }
    if (v === 'documents')     { setDkey(null); setTkey(null); }
    if (v === 'tables')        { setTkey(null); }
    setView(v);
    if (extra.jkey !== undefined) setJkey(extra.jkey);
    if (extra.dkey !== undefined) setDkey(extra.dkey);
    if (extra.tkey !== undefined) setTkey(extra.tkey);
  };

  return (
    <div className="px-5 py-4" data-testid="spec-catalog-page" style={{ color: t.text }}>
      {/* Header / breadcrumb */}
      <div className="flex items-start gap-3 mb-4">
        <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: t.accent }} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold tracking-wider" style={{ color: t.accent }}>
            SPEC LIBRARY{project ? ` · ${(project.asset_name || project.name || '').toUpperCase()}` : ''}
          </div>
          <h2 className="text-base font-bold">Project Specifications</h2>
          <Breadcrumb t={t} view={view} jkey={jkey} dkey={dkey} tkey={tkey} onGo={go} />
        </div>
        {/* PT ⇄ EN toggle */}
        <div className="flex gap-1 p-0.5 rounded text-[11px] font-semibold" style={{ background: t.inputBg, border: `1px solid ${t.cardBorder}` }}>
          <Languages className="w-3 h-3 mx-1 mt-0.5" style={{ color: t.textDim }} />
          {['pt', 'en'].map(code => (
            <button key={code} onClick={() => setLang(code)}
              className="px-1.5 py-0.5 rounded transition-colors"
              style={{ background: lang === code ? t.accent : 'transparent', color: lang === code ? '#000' : t.textMuted }}
              data-testid={`spec-lang-${code}`}>
              {code.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {!project && (
        <InfoBanner t={t}>
          Select a project in the sidebar to apply these specs. You can still browse the catalog here.
        </InfoBanner>
      )}

      {view === 'jurisdictions' && <JurisdictionsGrid t={t} project={project} onPick={k => go('documents', { jkey: k })} onRefresh={() => setRefreshKey(k => k + 1)} refreshKey={refreshKey} />}
      {view === 'documents'     && <DocumentsList t={t} jkey={jkey} lang={lang} project={project} onBack={() => go('jurisdictions')} onPick={k => go('tables', { dkey: k })} />}
      {view === 'tables'        && <TablesList t={t} dkey={dkey} lang={lang} project={project} onBack={() => go('documents', { jkey })} onPick={k => go('table', { tkey: k })} />}
      {view === 'table'         && <TableDetail t={t} tkey={tkey} project={project} lang={lang} onBack={() => go('tables', { dkey })} />}
    </div>
  );
}

/* ───────────── breadcrumb ───────────── */
function Breadcrumb({ t, view, jkey, dkey, tkey, onGo }) {
  const crumbs = [
    { label: 'Jurisdictions', onClick: () => onGo('jurisdictions'), active: view === 'jurisdictions' },
  ];
  if (jkey) crumbs.push({ label: jkey, onClick: () => onGo('documents', { jkey }), active: view === 'documents' });
  if (dkey) crumbs.push({ label: dkey, onClick: () => onGo('tables', { dkey }), active: view === 'tables' });
  if (tkey) crumbs.push({ label: tkey, onClick: () => {}, active: view === 'table' });
  return (
    <div className="flex items-center gap-1 text-[11px] mt-1" style={{ color: t.textDim }}>
      {crumbs.map((c, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="w-2.5 h-2.5" />}
          <button onClick={c.onClick} className="hover:underline"
            style={{ color: c.active ? t.accent : t.textDim, fontWeight: c.active ? 600 : 400 }}>
            {c.label}
          </button>
        </span>
      ))}
    </div>
  );
}

function InfoBanner({ t, children }) {
  return (
    <div className="mb-3 px-3 py-2 rounded flex items-start gap-2 text-[11px]"
      style={{ background: t.inputBg, border: `1px solid ${t.cardBorder}`, color: t.textMuted }}>
      <Info className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: t.accent }} />
      <div>{children}</div>
    </div>
  );
}

/* ───────────── jurisdictions grid ───────────── */
function JurisdictionsGrid({ t, project, onPick, onRefresh, refreshKey }) {
  const [list, setList] = useState(null);
  const [docSelections, setDocSelections] = useState({});
  const [clearing, setClearing] = useState(false);
  const [msg, setMsg] = useState('');
  const totalTables = list ? list.reduce((a, j) => a + (j.selected_tables || 0), 0) : 0;
  const totalRubricas = list ? list.reduce((a, j) => a + (j.selected_rubricas || 0), 0) : 0;

  useEffect(() => {
    const url = project?.id
      ? `/api/prioris/spec-catalog/jurisdictions?project_id=${project.id}`
      : '/api/prioris/spec-catalog/jurisdictions';
    fetchSln(url).then(r => {
      setList(r.jurisdictions || []);
      setDocSelections(r.doc_selections || {});
    }).catch(() => setList([]));
  }, [project?.id, refreshKey]);

  const clearAll = async () => {
    if (!project?.id) return;
    if (!window.confirm('Remove ALL spec clauses applied to this project? This cannot be undone.')) return;
    setClearing(true); setMsg('');
    try {
      const r = await fetchSln(`/api/prioris/spec-catalog/project/${project.id}/selections`, { method: 'DELETE' });
      setMsg(`✓ Removed ${r.removed || 0} selection(s)`);
      if (onRefresh) onRefresh();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg(e.message); }
    setClearing(false);
  };

  if (!list) return <Spin t={t} />;

  return (
    <div data-testid="spec-jurisdictions-grid">
      {project?.id && totalTables > 0 && (
        <div className="mb-3 p-2 rounded-lg flex items-center gap-3" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <span className="text-[11px]" style={{ color: t.text }}>
            <strong>{totalTables}</strong> table{totalTables === 1 ? '' : 's'} applied · <strong>{totalRubricas}</strong> clause{totalRubricas === 1 ? '' : 's'} selected across all jurisdictions
          </span>
          <button onClick={clearAll} disabled={clearing}
            className="ml-auto px-2 py-1 rounded text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
            style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.4)' }}
            data-testid="spec-clear-all-project">
            {clearing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            Remove all project specs
          </button>
          {msg && <span className="text-[11px]" style={{ color: msg.startsWith('✓') ? '#22c55e' : '#f87171' }}>{msg}</span>}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map(j => {
          const soon = j.coming_soon;
          const hasSel = (j.selected_tables || 0) > 0;
          return (
            <button key={j.key} disabled={soon}
              onClick={() => onPick(j.key)}
              className="text-left p-4 rounded-lg transition-all hover:-translate-y-0.5"
              style={{
                background: t.card,
                border: `1px solid ${hasSel ? t.accent : t.cardBorder}`,
                opacity: soon ? 0.5 : 1, cursor: soon ? 'not-allowed' : 'pointer',
              }}
              data-testid={`spec-jurisdiction-${j.key}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{j.flag}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-bold truncate">{j.label}</div>
                  <div className="text-[10px]" style={{ color: t.textDim }}>{j.country} · {j.key}</div>
                </div>
                {soon && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold"
                    style={{ background: t.inputBg, color: t.textDim, border: `1px solid ${t.cardBorder}` }}>
                    SOON
                  </span>
                )}
                {hasSel && !soon && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                    style={{ background: t.accentBg, color: t.accent }}>
                    {j.selected_tables} · {j.selected_rubricas}
                  </span>
                )}
              </div>
              <div className="text-[11px]" style={{ color: t.textMuted }}>
                {soon
                  ? 'Coming in a future release — concrete specs first'
                  : hasSel
                    ? `${j.selected_tables} table(s) applied · ${j.selected_rubricas} rubrica(s) selected`
                    : `${j.document_count} document${j.document_count === 1 ? '' : 's'} available`}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────── documents list ───────────── */
function DocumentsList({ t, jkey, lang, project, onBack, onPick }) {
  const [j, setJ] = useState(null);
  const [docSel, setDocSel] = useState({});  // {document_key: {tables, rubricas}}

  useEffect(() => {
    fetchSln(`/api/prioris/spec-catalog/jurisdictions/${jkey}`).then(setJ).catch(() => setJ(null));
    if (project?.id) {
      fetchSln(`/api/prioris/spec-catalog/jurisdictions?project_id=${project.id}`)
        .then(r => setDocSel(r.doc_selections || {}))
        .catch(() => setDocSel({}));
    }
  }, [jkey, project?.id]);

  if (!j) return <Spin t={t} />;
  return (
    <div data-testid="spec-documents-list">
      <BackBar t={t} label={lang === 'en' ? 'Jurisdictions' : 'Jurisdições'} onBack={onBack} />
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">{j.flag}</span>
        <div>
          <div className="text-[13px] font-bold">{j.label}</div>
          <div className="text-[11px]" style={{ color: t.textDim }}>{j.country} · {j.documents?.length || 0} {lang === 'en' ? 'document(s)' : 'documento(s)'}</div>
        </div>
      </div>
      {(j.documents || []).map(d => {
        const sel = docSel[d.key];
        return (
          <button key={d.key} onClick={() => onPick(d.key)}
            className="w-full text-left p-3 rounded-lg mb-2 flex items-center gap-3 hover:-translate-y-0.5 transition-transform"
            style={{ background: t.card, border: `1px solid ${sel ? t.accent : t.cardBorder}` }}
            data-testid={`spec-doc-${d.key}`}>
            <BookOpenCheck className="w-4 h-4" style={{ color: t.accent }} />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold">{lang === 'en' ? (d.label_en || d.label) : d.label}</div>
              <div className="text-[11px]" style={{ color: t.textDim }}>{lang === 'en' ? (d.description_en || d.description) : (d.description || '—')}</div>
            </div>
            {sel && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                style={{ background: t.accentBg, color: t.accent }}
                title={lang === 'en' ? `${sel.tables} table(s) · ${sel.rubricas} clause(s)` : `${sel.tables} quadro(s) · ${sel.rubricas} rubrica(s)`}>
                {sel.tables} · {sel.rubricas}
              </span>
            )}
            <span className="text-[10px]" style={{ color: t.textDim }}>{d.table_count} {lang === 'en' ? 'table(s)' : 'quadro(s)'}</span>
            <ChevronRight className="w-3.5 h-3.5" style={{ color: t.textMuted }} />
          </button>
        );
      })}
      {(j.documents || []).length === 0 && (
        <EmptyBox t={t} label="No documents yet" />
      )}
    </div>
  );
}

/* ───────────── tables list (grouped by section, bulk-apply) ───────────── */
function TablesList({ t, dkey, lang, project, onBack, onPick }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [d, setD] = useState(null);
  const [bulkBusy, setBulkBusy] = useState('');
  const [bulkMsg, setBulkMsg] = useState('');

  const load = () => fetchSln(`/api/prioris/spec-catalog/documents/${dkey}`).then(setD).catch(() => setD(null));
  useEffect(() => { load(); }, [dkey]);

  const applyBulk = async (section_key) => {
    if (!project?.id) { alert('Select a project in the sidebar first.'); return; }
    const label = section_key || 'entire document';
    if (!window.confirm(`Apply ${label} to this project? All listed clauses will be marked as applicable.`)) return;
    setBulkBusy(section_key || '*'); setBulkMsg('');
    try {
      const res = await fetchSln(`/api/prioris/spec-catalog/project/${project.id}/selections/bulk`, {
        method: 'POST',
        body: JSON.stringify({ document_key: dkey, section_key: section_key || null }),
      });
      setBulkMsg(`✓ ${res.tables_applied} table(s) applied`);
      setTimeout(() => setBulkMsg(''), 3000);
    } catch (e) { setBulkMsg(e.message); }
    setBulkBusy('');
  };

  if (!d) return <Spin t={t} />;
  const sections = d.sections || [];
  // group tables by section key
  const tablesBySection = {};
  for (const tb of d.tables || []) {
    const k = tb.section_key || '_other';
    (tablesBySection[k] = tablesBySection[k] || []).push(tb);
  }
  const otherTables = tablesBySection._other || [];

  return (
    <div data-testid="spec-tables-list">
      <BackBar t={t} label={lang === 'en' ? 'Jurisdiction' : 'Jurisdictions'} onBack={onBack} />
      <div className="mb-3 flex items-start gap-3">
        <div className="flex-1">
          <div className="text-[13px] font-bold">{lang === 'en' ? (d.label_en || d.label) : d.label}</div>
          <div className="text-[11px]" style={{ color: t.textDim }}>{lang === 'en' ? (d.description_en || d.description) : d.description}</div>
        </div>
        {isAdmin && project?.id && (
          <button onClick={() => applyBulk(null)} disabled={bulkBusy === '*'}
            className="px-2 py-1 rounded text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
            style={{ background: t.accent, color: '#000' }} data-testid="bulk-apply-document">
            {bulkBusy === '*' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {lang === 'en' ? 'Apply entire document' : 'Aplicar documento inteiro'}
          </button>
        )}
      </div>
      {bulkMsg && <div className="mb-2 text-[11px]" style={{ color: bulkMsg.startsWith('✓') ? '#22c55e' : '#f87171' }}>{bulkMsg}</div>}

      {/* render each section with its tables */}
      {sections.map(sec => {
        const tbls = tablesBySection[sec.key] || [];
        if (tbls.length === 0) return null;
        return (
          <div key={sec.key} className="mb-3 rounded-lg" style={{ border: `1px solid ${t.cardBorder}` }}>
            <div className="flex items-center gap-2 px-3 py-2" style={{ background: t.inputBg, borderBottom: `1px solid ${t.cardBorder}` }}>
              <Layers className="w-3 h-3" style={{ color: t.accent }} />
              <div className="flex-1 min-w-0 text-[12px] font-bold">{lang === 'en' ? (sec.label_en || sec.label) : sec.label}</div>
              <span className="text-[10px]" style={{ color: t.textDim }}>{tbls.length} {lang === 'en' ? 'table(s)' : 'quadros'}</span>
              {isAdmin && project?.id && tbls.some(tb => !tb.stub) && (
                <button onClick={() => applyBulk(sec.key)} disabled={bulkBusy === sec.key}
                  className="px-2 py-0.5 rounded text-[10px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                  style={{ background: t.accentBg, color: t.accent, border: `1px solid ${t.accent}` }}
                  data-testid={`bulk-apply-section-${sec.key}`}>
                  {bulkBusy === sec.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {lang === 'en' ? 'Apply section' : 'Aplicar secção'}
                </button>
              )}
            </div>
            <div className="p-1.5 space-y-1.5">
              {tbls.map(tb => <TableRow key={tb.key} tb={tb} t={t} lang={lang} onPick={onPick} />)}
            </div>
          </div>
        );
      })}

      {otherTables.length > 0 && (
        <div className="space-y-1.5">
          {otherTables.map(tb => <TableRow key={tb.key} tb={tb} t={t} lang={lang} onPick={onPick} />)}
        </div>
      )}
    </div>
  );
}

function TableRow({ tb, t, lang, onPick }) {
  const isStub = tb.stub;
  return (
    <button onClick={() => onPick(tb.key)}
      className="w-full text-left p-2.5 rounded-lg flex items-center gap-3 transition-transform hover:-translate-y-0.5"
      style={{ background: t.card, border: `1px solid ${t.cardBorder}`, opacity: isStub ? 0.75 : 1 }}
      data-testid={`spec-table-${tb.key}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold font-mono"
            style={{ background: t.accentBg, color: t.accent }}>{tb.key}</span>
          <span className="text-[12px] font-semibold truncate">{lang === 'en' ? (tb.label_en || tb.label) : tb.label}</span>
          {isStub && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold"
              style={{ background: t.inputBg, color: t.textDim, border: `1px solid ${t.cardBorder}` }}>
              {lang === 'en' ? 'IN PREP' : 'EM PREPARAÇÃO'}
            </span>
          )}
        </div>
        <div className="text-[11px] mt-1 flex gap-3 flex-wrap" style={{ color: t.textDim }}>
          {tb.family && <span>Family: <code>{tb.family}</code></span>}
          {tb.standard && <span>Norma: {tb.standard}</span>}
          <span>{tb.rubrica_count} {lang === 'en' ? 'clauses' : 'rubricas'}</span>
          <span>{tb.property_count} {lang === 'en' ? 'properties' : 'propriedades'}</span>
        </div>
      </div>
      <ChevronRight className="w-3.5 h-3.5" style={{ color: t.textMuted }} />
    </button>
  );
}

/* ───────────── table detail (rubrica picker + matrix) ───────────── */
function TableDetail({ t, tkey, project, lang, onBack }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tbl, setTbl] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSln(`/api/prioris/spec-catalog/tables/${tkey}`).then(setTbl).catch(() => setTbl(null));
    if (project?.id) {
      fetchSln(`/api/prioris/spec-catalog/project/${project.id}/selections`).then(r => {
        const existing = (r.selections || []).find(s => s.table_key === tkey);
        if (existing) setSelected(new Set(existing.selected_rubricas));
      }).catch(() => {});
    }
  }, [tkey, project?.id]);

  if (!tbl) return <Spin t={t} />;

  const toggle = (code) => {
    const next = new Set(selected);
    next.has(code) ? next.delete(code) : next.add(code);
    setSelected(next);
    setSaved(false);
  };
  const pickAll = () => { setSelected(new Set(tbl.rubricas.map(r => r.code))); setSaved(false); };
  const clearAll = () => { setSelected(new Set()); setSaved(false); };

  const applyToProject = async () => {
    if (!project?.id) { alert('Select a project in the sidebar first.'); return; }
    setSaving(true);
    try {
      await fetchSln('/api/prioris/spec-catalog/project/selections', {
        method: 'POST',
        body: JSON.stringify({
          project_id: project.id, table_key: tkey,
          selected_rubricas: Array.from(selected),
        }),
      });
      setSaved(true);
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  // Properties matrix: only show columns for visible rubricas; hide all if none selected yet
  const visibleRubs = selected.size > 0 ? tbl.rubricas.filter(r => selected.has(r.code)) : tbl.rubricas;

  return (
    <div data-testid="spec-table-detail">
      <BackBar t={t} label="Tables" onBack={onBack} />

      <div className="mb-3 p-3 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="flex items-start gap-2">
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: t.accentBg, color: t.accent }}>{tbl.key}</span>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-bold leading-tight">{lang === 'en' ? (tbl.label_en || tbl.label) : tbl.label}</div>
            <div className="text-[11px] mt-0.5" style={{ color: t.textDim }}>
              {tbl.jurisdiction_label} · {lang === 'en' ? (tbl.document_label_en || tbl.document_label) : tbl.document_label} · {lang === 'en' ? 'family' : 'família'}: <code>{tbl.family}</code> · {lang === 'en' ? 'standard' : 'norma'}: {tbl.standard}
            </div>
          </div>
          {tbl.stub && (
            <span className="px-2 py-0.5 rounded text-[10px] font-bold" style={{ background: t.inputBg, color: t.textMuted, border: `1px solid ${t.cardBorder}` }}>
              {lang === 'en' ? 'IN PREPARATION' : 'EM PREPARAÇÃO'}
            </span>
          )}
        </div>
        {tbl.stub && (
          <div className="mt-2 text-[11px]" style={{ color: t.textDim }}>
            <Info className="w-3 h-3 inline mr-1" />
            {lang === 'en'
              ? 'This Quadro is seeded structurally but numeric values are not yet populated. You can still apply its clauses to the project — detailed evaluation will unlock once we seed the values.'
              : 'Este Quadro está estruturado mas os valores numéricos ainda não foram populados. Pode aplicar as rubricas ao projecto — a avaliação detalhada ficará disponível quando populármos os valores.'}
          </div>
        )}
      </div>

      {/* Rubrica picker */}
      <div className="mb-3 p-3 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="flex items-center gap-2 mb-2">
          <CheckSquare className="w-3.5 h-3.5" style={{ color: t.accent }} />
          <span className="text-[12px] font-bold">{lang === 'en' ? 'Clauses applicable to this project' : 'Rubricas aplicáveis a este projecto'}</span>
          <span className="ml-auto text-[11px]" style={{ color: t.textDim }}>{selected.size} / {tbl.rubricas.length} {lang === 'en' ? 'selected' : 'seleccionadas'}</span>
          <button onClick={pickAll} className="text-[11px] px-2 py-0.5 rounded" style={{ background: t.inputBg, color: t.textMuted }} data-testid="spec-select-all">{lang === 'en' ? 'Select all' : 'Todas'}</button>
          <button onClick={clearAll} className="text-[11px] px-2 py-0.5 rounded" style={{ background: t.inputBg, color: t.textMuted }} data-testid="spec-clear-all">{lang === 'en' ? 'Clear' : 'Limpar'}</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {tbl.rubricas.map(r => {
            const on = selected.has(r.code);
            return (
              <button key={r.code} onClick={() => toggle(r.code)}
                className="text-left px-2 py-1.5 rounded flex items-center gap-2 transition-colors"
                style={{
                  background: on ? t.accentBg : t.inputBg,
                  border: `1px solid ${on ? t.accent : t.inputBorder}`,
                }}
                data-testid={`spec-rubrica-${r.code}`}>
                {on ? <CheckSquare className="w-3 h-3" style={{ color: t.accent }} />
                    : <Square      className="w-3 h-3" style={{ color: t.textDim }} />}
                <span className="text-[11px] font-mono" style={{ color: on ? t.accent : t.textMuted }}>{r.code}</span>
                <span className="text-[12px] flex-1 min-w-0 truncate" style={{ color: on ? t.text : t.textMuted }}>{lang === 'en' ? (r.label_en || r.label) : r.label}</span>
                <span className="text-[10px]" style={{ color: t.textDim }}>{r.type}</span>
              </button>
            );
          })}
        </div>
        {isAdmin && (
          <div className="mt-3 flex items-center gap-2">
            <button onClick={applyToProject} disabled={saving || !project?.id}
              className="px-3 py-1 rounded text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
              style={{ background: t.accent, color: '#000' }}
              data-testid="spec-apply-project">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {lang === 'en' ? 'Apply to project' : 'Aplicar ao projecto'}
            </button>
            {saved && <span className="text-[11px]" style={{ color: '#22c55e' }}>✓ {lang === 'en' ? 'Saved' : 'Guardado'}</span>}
            <span className="ml-auto text-[11px]" style={{ color: t.textDim }}>
              <Info className="w-3 h-3 inline mr-1" />
              {lang === 'en'
                ? <>Supplier / material evaluation happens in the <strong style={{ color: t.text }}>Materials Approval</strong> module.</>
                : <>A avaliação de fornecedores / materiais acontece no módulo <strong style={{ color: t.text }}>Materials Approval</strong>.</>}
            </span>
          </div>
        )}
        {!isAdmin && <div className="text-[11px] mt-2" style={{ color: t.textDim }}>{lang === 'en' ? 'Admin only: applying clauses to a project requires admin access.' : 'Apenas admin: aplicar rubricas requer permissão de admin.'}</div>}
      </div>

      {/* Properties matrix (rows × cols exactly like the published spec) */}
      {!tbl.stub && tbl.properties && tbl.properties.length > 0 && (
        <PropertiesMatrix t={t} tbl={tbl} visibleRubs={visibleRubs} lang={lang} />
      )}
      {tbl.stub && (
        <div className="border border-dashed rounded-lg p-6 text-center text-[11px]" style={{ borderColor: t.cardBorder, color: t.textDim }}>
          {lang === 'en' ? 'Property matrix coming soon. Clauses above are already usable.' : 'Matriz de propriedades em preparação. As rubricas acima já podem ser aplicadas.'}
        </div>
      )}

      {/* Footnotes */}
      {tbl.footnotes && Object.keys(tbl.footnotes).length > 0 && (
        <div className="mt-3 p-3 rounded-lg text-[11px]"
          style={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.textMuted }}>
          <div className="font-bold mb-1 text-[12px]" style={{ color: t.text }}>Notas</div>
          {Object.entries(tbl.footnotes).map(([k, v]) => (
            <div key={k} className="mb-1"><strong style={{ color: t.accent }}>({k})</strong> {v}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────── properties matrix ───────────── */
function PropertiesMatrix({ t, tbl, visibleRubs, lang }) {
  // "A declarar" is a PT-CE-EP specific term; every other jurisdiction uses "To be declared".
  const declareLabel = tbl.jurisdiction_key === 'PT-EP' ? 'A declarar' : 'To be declared';
  return (
    <div className="rounded-lg overflow-auto" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }} data-testid="spec-properties-matrix">
      <table className="w-full text-[11px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr style={{ background: t.inputBg }}>
            <th className="text-left px-2 py-2 sticky left-0 z-10" style={{ background: t.inputBg, borderBottom: `1px solid ${t.cardBorder}`, minWidth: 220 }}>
              <div className="text-[11px] font-bold" style={{ color: t.text }}>{lang === 'en' ? 'Requirements / Properties' : 'Requisitos / Propriedades'}</div>
            </th>
            <th className="px-2 py-2 text-center" style={{ borderBottom: `1px solid ${t.cardBorder}`, minWidth: 80 }}>
              <div className="text-[10px] font-semibold" style={{ color: t.textMuted }}>{lang === 'en' ? 'Standard ref.' : 'Refª normativa'}</div>
            </th>
            <th className="px-2 py-2 text-center" style={{ borderBottom: `1px solid ${t.cardBorder}`, minWidth: 50 }}>
              <div className="text-[10px] font-semibold" style={{ color: t.textMuted }}>{lang === 'en' ? 'Unit' : 'Unid.'}</div>
            </th>
            {visibleRubs.map(r => (
              <th key={r.code} className="px-2 py-2 text-center" style={{ borderBottom: `1px solid ${t.cardBorder}`, minWidth: 120 }}>
                <div className="text-[11px] font-bold leading-tight" style={{ color: t.text }}>{lang === 'en' ? (r.label_en || r.label) : r.label}</div>
                <div className="text-[9px] mt-0.5" style={{ color: t.textDim }}>{r.type}</div>
                <div className="text-[10px] font-mono mt-0.5" style={{ color: t.accent }}>{r.code}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tbl.properties.map((p, i) => (
            <tr key={p.key} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
              <td className="px-2 py-2 sticky left-0"
                style={{ background: i % 2 === 0 ? t.card : 'rgba(255,255,255,0.02)', borderBottom: `1px solid ${t.cardBorder}`, verticalAlign: 'top' }}>
                <div className="font-semibold leading-tight" style={{ color: t.text }}>{lang === 'en' ? (p.label_en || p.label) : p.label}</div>
                {p.help && <div className="text-[10px] mt-0.5" style={{ color: t.textDim }}>{p.help}</div>}
              </td>
              <td className="px-2 py-2 text-center" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textMuted, verticalAlign: 'top' }}>{p.ref_normativa}</td>
              <td className="px-2 py-2 text-center" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textMuted, verticalAlign: 'top' }}>{p.unit}</td>
              {visibleRubs.map(r => (
                <td key={r.code} className="px-2 py-2 text-center"
                  style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.text, verticalAlign: 'top' }}>
                  <CellDisplay t={t} cell={p.values[r.code]} declareLabel={declareLabel} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CellDisplay({ t, cell, declareLabel }) {
  if (!cell || cell.na) return <span style={{ color: t.textDim }}>NA</span>;
  if (cell.declare_only) return <span style={{ color: '#eab308' }}>{declareLabel || 'To be declared'}</span>;

  if (cell.conditional === 'se_mb') {
    return (
      <div className="leading-tight">
        <div>SE ≥ <strong>{cell.se_threshold}</strong></div>
        <div style={{ color: t.textDim, fontSize: 9 }}>MB ≤ {cell.mb_threshold} se SE &lt; {cell.se_threshold}</div>
      </div>
    );
  }
  if (cell.conditional === 'sonnenbrand') {
    return <div style={{ fontSize: 9, color: t.textMuted }}>Perda ≤ {cell.loss_max}<br/>SBLA ≤ {cell.sbla_max}</div>;
  }
  if (cell.conditional === 'freeze_thaw') {
    return (
      <div style={{ fontSize: 9, color: t.textMuted }} className="leading-tight">
        Se WA₂₄ &gt; {cell.wa_trigger}:<br/><strong style={{ color: t.text }}>{cell.ms_category}</strong>
        {cell.note && <sup style={{ color: t.accent }}>({cell.note})</sup>}
      </div>
    );
  }
  if (cell.category) {
    return (
      <span className="font-mono" style={{ color: t.text }}>
        {cell.category}{cell.note && <sup style={{ color: t.accent }}>({cell.note})</sup>}
      </span>
    );
  }
  if (cell.op && cell.value !== undefined) {
    const opSym = { '<=': '≤', '>=': '≥', '=': '=', '!=': '≠' }[cell.op] || cell.op;
    return (
      <span>
        {opSym} {cell.value}{cell.note && <sup style={{ color: t.accent }}>({cell.note})</sup>}
      </span>
    );
  }
  return <span style={{ color: t.textDim }}>—</span>;
}

/* ───────────── helpers ───────────── */
function BackBar({ t, label, onBack }) {
  return (
    <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-[11px] hover:underline" style={{ color: t.accent }}>
      <ArrowLeft className="w-3 h-3" /> Back to {label}
    </button>
  );
}
function Spin({ t }) {
  return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: t.accent }} /></div>;
}
function EmptyBox({ t, label }) {
  return (
    <div className="border border-dashed rounded-lg py-8 text-center text-[12px]" style={{ borderColor: t.cardBorder, color: t.textDim }}>
      {label}
    </div>
  );
}
